import { useTranslation } from "react-i18next";
import { PERMISSION_LEVEL_META } from "@/components/permission-level";
import { useMemo, useState } from "react";
import { Archive, Check, Link2, Settings2, Trash2 } from "lucide-react";
import { MANUAL_STATES, type ManualState, type FacilityPermissionLevel } from "@arc/contracts";
import type { AuthSession } from "../../auth/keycloak.js";
import { Button } from "@/components/ui/button";
import { useFacilityManuals } from "./useFacilityManuals.js";
import type { ManualBinKind } from "./ManualBinDialog.js";

/**
 * The four ADR-018 manual states, translated.
 *
 * Kept as a key BUILDER rather than a label map so adding a state without its translation is a missing-key
 * failure rather than `undefined` rendered into the UI. All four live in the app layer together: the legacy has
 * `status.draft` and `status.readyForReview` with near-identical wording, but `confirmed` and `marked` are
 * ADR-018 concepts it never had, and splitting four states across two layers costs more than the duplication.
 */
const stateKey = (s: ManualState) => `facilityManagement.manualState.${s}` as const;


/**
 * The Overview body of the {@link FacilityDetailsDrawer} (ADR-016 §C) — the facility "at a glance" for ANY viewer: a
 * manual state-count summary, the caller's effective access, and a copy-link action, plus a gated "Manage area" entry
 * that switches the drawer to Manage mode.
 *
 * Counts are computed CLIENT-SIDE from the facility's manuals (`useFacilityManuals`, active + archived) — the
 * **unfiltered** query lists (not the manuals view's toolbar filter/search), so they are facility totals, never the
 * searched subset (panel #4). While EITHER query is pending or errored we render a loading/error state — a missing
 * query is **not** a real `0` (panel #2).
 */
export function FacilityOverview({
  facilityId,
  permission,
  canManage,
  session,
  onManage,
  canManageManuals,
  onOpenBin,
}: {
  facilityId: number;
  /** The caller's effective permission on this facility (drives the access label). */
  permission: FacilityPermissionLevel | null;
  /** Facility-management capability (manage/access_manage/bypass) — computed by the caller, NEVER the manuals boolean. */
  canManage: boolean;
  session: AuthSession | null;
  /** Switch the drawer to Manage mode (only invoked from the gated button). */
  onManage: () => void;
  /** Manuals capability: the trash needs `manuals_manage`, so its button needs it too (ADR-056). */
  canManageManuals: boolean;
  /** Open the list of archived manuals or the trash. */
  onOpenBin: (kind: ManualBinKind) => void;
}) {
  const { t } = useTranslation();
  const active = useFacilityManuals(session, facilityId, false);
  const archived = useFacilityManuals(session, facilityId, true);
  const [copied, setCopied] = useState(false);

  const activeList = active.data ?? [];
  const counts = useMemo(() => {
    const m: Record<ManualState, number> = { draft: 0, ready_for_review: 0, confirmed: 0, marked: 0 };
    for (const x of activeList) m[x.state] += 1;
    return m;
  }, [activeList]);

  // BOTH queries must have succeeded before the summary is trustworthy — else loading/error, not confident zeros.
  const loading = active.isPending || archived.isPending;
  const errored = active.isError || archived.isError;
  const archivedCount = archived.data?.length ?? 0;

  const copyLink = async () => {
    // The canonical facility deep link — only the facility param, not the current search/tab state (panel/ADR §C).
    const url = `${window.location.origin}${window.location.pathname}?arc_facility=${facilityId}`;
    // Guard the API FIRST: in an insecure context `navigator.clipboard` is undefined and `?.writeText` would
    // short-circuit to `undefined` WITHOUT throwing — so a naive try/catch would still flash t("facilityManagement.overview.linkCopied") though
    // nothing was written (diff panel #1). Only claim success after the write actually resolves.
    if (!navigator.clipboard) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      /* permission denied — no-op; never show a false "copied" state */
    }
  };

  return (
    <div className="flex flex-col gap-5">
      {/* Manuals at a glance — the active state breakdown + the archived total. */}
      <section aria-label={t("facilityManagement.overview.region")} className="flex flex-col gap-2">
        <h3 className="text-sm font-semibold">{t("facilityManagement.overview.manuals")}</h3>
        {loading ? (
          <p className="text-muted-foreground text-sm">{t("facilityManagement.overview.loading")}</p>
        ) : errored ? (
          <p className="text-muted-foreground text-sm">{t("facilityManagement.overview.loadFailed")}</p>
        ) : (
          <dl className="flex flex-col gap-1 text-sm">
            {MANUAL_STATES.map((s) => (
              <div key={s} className="flex items-center justify-between gap-3">
                <dt className="text-muted-foreground">{t(stateKey(s))}</dt>
                <dd className="font-medium tabular-nums">{counts[s]}</dd>
              </div>
            ))}
            <div className="border-border mt-1 flex items-center justify-between gap-3 border-t pt-1">
              <dt className="text-muted-foreground inline-flex items-center gap-1.5">
                <Archive aria-hidden="true" className="size-3.5" /> {t("facilityManagement.overview.archived")}
              </dt>
              <dd className="font-medium tabular-nums">{archivedCount}</dd>
            </div>
          </dl>
        )}
      </section>

      {/* The caller's effective access on this facility (the real ladder). */}
      <section aria-label={t("facilityManagement.overview.yourAccess")} className="flex items-center justify-between gap-3 text-sm">
        <span className="text-muted-foreground">{t("facilityManagement.overview.yourAccess")}</span>
        <span className="font-medium">{permission ? t(PERMISSION_LEVEL_META[permission].labelKey) : "—"}</span>
      </section>

      {/* Actions. Copy-link is available to anyone with view; Manage area only when the caller can manage the facility. The
          two lists sit at the bottom (owner, 2026-11-02); the trash only for those who may manage manuals. */}
      <section aria-label={t("facilityManagement.overview.areaActions")} className="flex flex-col gap-2">
        <Button variant="outline" size="sm" onPress={() => void copyLink()} className="justify-start">
          {copied ? <Check aria-hidden="true" /> : <Link2 aria-hidden="true" />}
          {copied ? t("facilityManagement.overview.linkCopied") : t("facilityManagement.overview.copyLink")}
        </Button>
        {canManage ? (
          <Button variant="outline" size="sm" onPress={onManage} className="justify-start">
            <Settings2 aria-hidden="true" />
            {t("facilityManagement.overview.manageArea")}
          </Button>
        ) : null}
        <Button variant="outline" size="sm" onPress={() => onOpenBin("archived")} className="justify-start">
          <Archive aria-hidden="true" />
          {loading || errored ? t("facilityManagement.overview.showArchivedPlain") : t("facilityManagement.overview.showArchived", { count: archivedCount })}
        </Button>
        {canManageManuals ? (
          <Button variant="outline" size="sm" onPress={() => onOpenBin("trash")} className="justify-start">
            <Trash2 aria-hidden="true" />
            {t("manualSelection.facilitySidebar.openTrash")}
          </Button>
        ) : null}
      </section>
    </div>
  );
}
